import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import ProGate from "@/components/ProGate";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, ScrollText, Wallet, RefreshCw, X, ListTree } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtUsd, fmtPct, fmtPrice, CHAIN_META } from "@/lib/format";
import WalletImportDialog from "@/components/WalletImportDialog";
import TradeFillsDialog, { fmtMcap, fmtHold } from "@/components/TradeFillsDialog";

const MOODS = [
  { v: "🚀", label: "Conviction" }, { v: "😬", label: "FOMO" }, { v: "🎰", label: "Gamble" },
  { v: "🧠", label: "Research" }, { v: "💎", label: "Diamond" }, { v: "😴", label: "Boring" },
];
const CHAINS = ["solana", "ethereum", "bsc", "base", "robinhood"];
const empty = { ticker: "", name: "", address: "", chain: "solana", status: "open", buyPrice: "", sellPrice: "", amount: "", mood: "🚀", notes: "", date: "" };

function TradeDialog({ open, setOpen, initial, onSaved }) {
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ ...empty, ...initial }); }, [open, initial]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.ticker || !f.buyPrice || !f.amount) { toast.error("Ticker, buy price and amount are required"); return; }
    setSaving(true);
    const body = {
      ticker: f.ticker, name: f.name || "", address: (f.address || "").trim(), chain: f.chain, status: f.status, mood: f.mood, notes: f.notes,
      buyPrice: parseFloat(f.buyPrice), amount: parseFloat(f.amount),
      sellPrice: f.status === "closed" && f.sellPrice !== "" ? parseFloat(f.sellPrice) : null,
      date: f.date ? new Date(f.date).toISOString() : null,
    };
    try {
      if (f.id) await api.put(`/trades/${f.id}`, body);
      else await api.post("/trades", body);
      toast.success(f.id ? "Trade updated" : "Trade logged");
      setOpen(false); onSaved();
    } catch (err) {
      if (err.response?.status === 402) toast.error("Trade Journal is a Pro feature", { action: { label: "Go Pro", onClick: () => (window.location.href = "/pricing") } });
      else toast.error("Could not save trade");
    }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="glass border-[#334566] max-w-lg" data-testid="trade-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{f.id ? "Edit trade" : "Log a trade"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Ticker</Label>
            <Input value={f.ticker} onChange={(e) => set("ticker", e.target.value)} placeholder="BONK" className="bg-[#0d1420] border-[#334566]" data-testid="trade-ticker" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Chain</Label>
            <Select value={f.chain} onValueChange={(v) => set("chain", v)}>
              <SelectTrigger className="bg-[#0d1420] border-[#334566]" data-testid="trade-chain"><SelectValue /></SelectTrigger>
              <SelectContent>{CHAINS.map((c) => <SelectItem key={c} value={c}>{CHAIN_META[c].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs text-slate-400">Contract address <span className="text-slate-600">(optional — enables live price in Stats)</span></Label>
            <Input value={f.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="Paste CA for exact live price match" className="bg-[#0d1420] border-[#334566] font-mono text-xs" data-testid="trade-address" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Buy price ($)</Label>
            <Input type="number" step="any" value={f.buyPrice} onChange={(e) => set("buyPrice", e.target.value)} placeholder="0.0000021" className="bg-[#0d1420] border-[#334566]" data-testid="trade-buy" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Amount invested ($)</Label>
            <Input type="number" step="any" value={f.amount} onChange={(e) => set("amount", e.target.value)} placeholder="500" className="bg-[#0d1420] border-[#334566]" data-testid="trade-amount" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Status</Label>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="bg-[#0d1420] border-[#334566]" data-testid="trade-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="open">Open</SelectItem><SelectItem value="closed">Closed</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Sell price ($)</Label>
            <Input type="number" step="any" disabled={f.status !== "closed"} value={f.sellPrice} onChange={(e) => set("sellPrice", e.target.value)} placeholder="0.0000045" className="bg-[#0d1420] border-[#334566] disabled:opacity-40" data-testid="trade-sell" />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs text-slate-400">Date</Label>
            <Input type="date" value={f.date ? f.date.slice(0, 10) : ""} onChange={(e) => set("date", e.target.value)} className="bg-[#0d1420] border-[#334566]" data-testid="trade-date" />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs text-slate-400">Mood</Label>
            <div className="flex flex-wrap gap-2">
              {MOODS.map((m) => (
                <button key={m.v} type="button" onClick={() => set("mood", m.v)}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${f.mood === m.v ? "border-emerald-500/60 bg-emerald-500/12" : "border-[#334566] bg-[#0d1420]"}`}>
                  {m.v} <span className="text-xs text-slate-400">{m.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs text-slate-400">Notes</Label>
            <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Why you aped, exit plan…" className="bg-[#0d1420] border-[#334566]" data-testid="trade-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="trade-save">
            {saving ? "Saving…" : f.id ? "Update trade" : "Log trade"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, color = "" }) {
  return (
    <Card className="bg-[#111723] border-[#222F43] p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono font-bold text-xl sm:text-2xl mt-1 ${color}`}>{value}</div>
    </Card>
  );
}

function JournalInner() {
  const location = useLocation();
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState(empty);
  const [walletOpen, setWalletOpen] = useState(false);
  const [wallets, setWallets] = useState([]);
  const [syncing, setSyncing] = useState(null);
  const [orphanPreview, setOrphanPreview] = useState(null);
  const [fillsFor, setFillsFor] = useState(null);
  const loadWallets = () => api.get("/trades/import/wallets").then(({ data }) => setWallets(data)).catch(() => {});
  const short = (a) => `${a.slice(0, 4)}…${a.slice(-4)}`;
  const syncWallet = async (addr) => {
    setSyncing(addr);
    try {
      const { data } = await api.post("/trades/import/wallet/sync", { address: addr });
      const parts = [];
      if (data.imported) parts.push(`${data.imported} new`);
      if (data.closed) parts.push(`${data.closed} closed`);
      if (data.updated) parts.push(`${data.updated} updated`);
      toast.success(parts.length ? `Synced ${short(addr)} · ${parts.join(" · ")}` : `Synced ${short(addr)} · nothing new`);
      load(); loadWallets();
      if (data.orphans?.length) {
        setOrphanPreview({ ...data, syncOnly: true, trades: data.orphans, buyFills: 0, sellFills: data.orphans.reduce((n, t) => n + t.sells, 0), note: "USD values use today's SOL price." });
        setWalletOpen(true);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(null); }
  };
  const forgetWallet = async (addr) => {
    await api.delete(`/trades/import/wallet/${addr}`);
    toast.success("Wallet removed (trades kept)");
    loadWallets();
  };

  const load = useCallback(async () => {
    const [t, s] = await Promise.all([api.get("/trades"), api.get("/trades/stats")]);
    setTrades(t.data); setStats(s.data);
  }, []);

  useEffect(() => { load(); loadWallets(); }, [load]);

  // prefill from scanner "Log Trade"
  useEffect(() => {
    const pf = location.state?.prefill;
    if (pf) { setInitial({ ...empty, ticker: pf.ticker, name: pf.name || "", address: pf.address || "", chain: pf.chain, buyPrice: String(pf.buyPrice || "") }); setOpen(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const del = async (id) => { await api.delete(`/trades/${id}`); toast.success("Trade deleted"); load(); };
  const edit = (t) => { setInitial({ ...t, buyPrice: String(t.buyPrice ?? ""), sellPrice: t.sellPrice != null ? String(t.sellPrice) : "", amount: String(t.amount ?? "") }); setOpen(true); };
  const addNew = () => { setInitial(empty); setOpen(true); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 sm:py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 sm:mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2"><ScrollText className="h-6 w-6 sm:h-7 sm:w-7 text-emerald-400" /> Trade Journal</h1>
          <p className="text-slate-400 text-sm mt-1">Log every ape. Track every P&L.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setWalletOpen(true)} variant="outline" className="tap border-[#334566] bg-transparent" aria-label="Import wallet" title="Import a Solana wallet" data-testid="import-wallet-button">
            <Wallet className="h-4 w-4 sm:mr-1.5 text-emerald-400" /> <span className="hidden sm:inline">Import wallet</span>
          </Button>
          <Button onClick={addNew} className="tap bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="add-trade-button">
            <Plus className="h-4 w-4 mr-1.5" /> Log Trade
          </Button>
        </div>
      </div>

      {wallets.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="wallets-bar">
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1"><Wallet className="h-3 w-3" /> Wallets</span>
          {wallets.map((w) => (
            <div key={w.address} className="flex items-center rounded-full border border-[#334566] bg-[#111723] pl-3 pr-1 h-8 text-xs" data-testid={`wallet-chip-${w.address}`}>
              <span className="font-mono text-slate-200">{w.label ? <><span className="text-emerald-400">{w.label}</span> <span className="text-slate-500">{short(w.address)}</span></> : short(w.address)}</span>
              {w.synced_at && <span className="hidden sm:inline text-slate-600 ml-2 font-mono">· {new Date(w.synced_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
              <button onClick={() => syncWallet(w.address)} disabled={syncing === w.address} title="Pull newest trades"
                className="tap ml-1.5 h-6 px-2 rounded-full bg-emerald-500/12 text-emerald-400 font-semibold flex items-center gap-1 disabled:opacity-60" data-testid={`wallet-sync-${w.address}`}>
                <RefreshCw className={`h-3 w-3 ${syncing === w.address ? "animate-spin" : ""}`} /> Sync
              </button>
              <button onClick={() => forgetWallet(w.address)} title="Remove wallet (keeps trades)" className="tap h-6 w-6 flex items-center justify-center text-slate-500 hover:text-red-400" data-testid={`wallet-forget-${w.address}`}><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6" data-testid="journal-stats">
          <StatCard label="Total P&L" value={fmtUsd(stats.totalPnl)} color={stats.totalPnl >= 0 ? "text-emerald-400" : "text-red-400"} />
          <StatCard label="Win Rate" value={`${stats.winRate}%`} color={stats.winRate >= 50 ? "text-emerald-400" : "text-amber-400"} />
          <StatCard label="Avg ROI" value={fmtPct(stats.avgRoi)} color={stats.avgRoi >= 0 ? "text-emerald-400" : "text-red-400"} />
          <StatCard label="Trades" value={stats.totalTrades} />
          <StatCard label="Open" value={stats.openTrades} color="text-cyan-400" />
          <StatCard label="W / L" value={`${stats.wins} / ${stats.losses}`} />
        </div>
      )}

      <Card className="bg-[#111723] border-[#222F43] overflow-hidden">
        {trades.length === 0 ? (
          <div className="p-10 sm:p-12 text-center text-slate-400" data-testid="no-trades">
            <p>No trades yet. Log your first ape 🚀</p>
            <button onClick={() => setWalletOpen(true)} className="mt-3 text-sm text-emerald-400 hover:underline" data-testid="no-trades-import">or import a Solana wallet in one paste →</button>
          </div>
        ) : (
          <>
          {/* Mobile: card rows */}
          <div className="md:hidden divide-y divide-[#222F43]" data-testid="journal-cards">
            {trades.map((t) => (
              <div key={t.id} className="p-4 flex items-start gap-3" data-testid={`trade-card-${t.ticker}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold">{t.mood} ${t.ticker}</span>
                    <Badge variant="outline" className={`text-[9px] uppercase bg-transparent px-1.5 py-0 ${t.status === "closed" ? "border-slate-600 text-slate-400" : "border-cyan-500/40 text-cyan-400"}`}>{t.status}</Badge>
                    {t.source === "wallet" && <Wallet className="h-3 w-3 text-slate-500" />}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                    <span style={{ color: CHAIN_META[t.chain]?.color }}>{CHAIN_META[t.chain]?.label || t.chain}</span> · {t.date ? t.date.slice(0, 10) : "—"} · in {fmtPrice(t.buyPrice)}{t.sellPrice != null ? ` → ${fmtPrice(t.sellPrice)}` : ""}
                  </div>
                  {(t.entryMcap || t.holdHours != null) && (
                    <button onClick={() => setFillsFor(t)} className="tap text-[11px] font-mono text-cyan-400/90 mt-0.5 text-left block" data-testid={`fills-m-${t.ticker}`}>
                      {t.entryMcap ? `entry ${fmtMcap(t.entryMcap)} mc` : ""}{t.status === "closed" && t.holdHours != null ? ` · held ${fmtHold(t.holdHours)}` : ""}{t.fills?.length ? ` · ${t.fills.length} fills ›` : ""}
                    </button>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm">{fmtUsd(t.amount)}</div>
                  {t.status === "closed"
                    ? <div className={`text-[11px] font-mono font-bold ${t.pnl > 0 ? "text-emerald-400" : t.pnl < 0 ? "text-red-400" : "text-slate-500"}`}>{fmtUsd(t.pnl)} · {fmtPct(t.roiPct)}</div>
                    : <div className="text-[11px] font-mono text-slate-500">open</div>}
                </div>
                <div className="flex flex-col gap-1 shrink-0 -mr-1">
                  <button onClick={() => edit(t)} className="tap p-1.5 text-slate-400" data-testid={`edit-${t.ticker}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(t.id)} className="tap p-1.5 text-slate-500" data-testid={`delete-${t.ticker}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#222F43] hover:bg-transparent">
                  <TableHead>Ticker</TableHead><TableHead>Chain</TableHead><TableHead>Status</TableHead>
                  <TableHead className="text-right">Buy</TableHead><TableHead className="text-right">Sell</TableHead>
                  <TableHead className="text-right">Size</TableHead><TableHead className="text-right">Entry MC</TableHead><TableHead className="text-right">Hold</TableHead><TableHead className="text-right">P&L</TableHead>
                  <TableHead className="text-right">ROI</TableHead><TableHead>Date</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trades.map((t) => (
                  <TableRow key={t.id} className="border-[#222F43]" data-testid={`trade-row-${t.ticker}`}>
                    <TableCell className="font-bold">{t.mood} ${t.ticker}{t.source === "wallet" && <Wallet className="inline h-3 w-3 ml-1.5 -mt-0.5 text-slate-500" title="Imported from wallet" />}</TableCell>
                    <TableCell className="text-xs uppercase text-slate-400">{CHAIN_META[t.chain]?.label || t.chain}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] uppercase bg-transparent ${t.status === "closed" ? "border-slate-600 text-slate-400" : "border-cyan-500/40 text-cyan-400"}`}>{t.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtPrice(t.buyPrice)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{t.sellPrice != null ? fmtPrice(t.sellPrice) : "—"}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtUsd(t.amount)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-slate-300" data-testid={`entry-mcap-${t.ticker}`}>{fmtMcap(t.entryMcap)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-slate-300">{t.status === "closed" ? fmtHold(t.holdHours) : "—"}</TableCell>
                    <TableCell className={`text-right font-mono font-bold ${t.pnl > 0 ? "text-emerald-400" : t.pnl < 0 ? "text-red-400" : "text-slate-500"}`}>{t.status === "closed" ? fmtUsd(t.pnl) : "—"}</TableCell>
                    <TableCell className={`text-right font-mono text-xs ${t.roiPct > 0 ? "text-emerald-400" : t.roiPct < 0 ? "text-red-400" : "text-slate-500"}`}>{t.status === "closed" ? fmtPct(t.roiPct) : "—"}</TableCell>
                    <TableCell className="text-xs text-slate-400">{t.date ? t.date.slice(0, 10) : "—"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        {(t.fills?.length > 0 || t.entryMcap) && <button onClick={() => setFillsFor(t)} className="p-1.5 text-slate-400 hover:text-cyan-400" title="Entries & exits" data-testid={`fills-${t.ticker}`}><ListTree className="h-3.5 w-3.5" /></button>}
                        <button onClick={() => edit(t)} className="p-1.5 text-slate-400 hover:text-emerald-400" data-testid={`edit-${t.ticker}`}><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => del(t.id)} className="p-1.5 text-slate-400 hover:text-red-400" data-testid={`delete-${t.ticker}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          </>
        )}
      </Card>

      <TradeDialog open={open} setOpen={setOpen} initial={initial} onSaved={load} />
      <WalletImportDialog open={walletOpen} setOpen={(o) => { setWalletOpen(o); if (!o) setOrphanPreview(null); }} onImported={() => { load(); loadWallets(); }} initialPreview={orphanPreview} />
      <TradeFillsDialog trade={fillsFor} onClose={() => setFillsFor(null)} />
    </div>
  );
}

export default function Journal() {
  return <ProGate feature="Trade Journal"><JournalInner /></ProGate>;
}
